import React, { useState, useEffect } from "react";
import { useParams, useRouteMatch, useHistory, Link } from "react-router-dom";
import { Tabs, Spin, Button, Form } from "antd";
import { Main } from "../styled";
import { PageHeader } from "../../components/page-headers/page-headers";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client";
import {
  useCarOwnerById,
  useOwnerCars,
  useOwnerProducts,
} from "./detailDataQueries";
import { useDispatch, useSelector } from "react-redux";
import { setAllCars } from "../../redux/cars/actionCreator";
import { setTableTab2Filer } from "../../redux/car_owners/actionCreator";
import {
  setCarOwnerAllProducts,
  setTableFilterCarOwnerProducts,
} from "../../redux/products/actionCreator";
import { setAllCarOwners } from "../../redux/car_owners/actionCreator";
import { UPDATE_CAR_OWNER } from "../../graphql/AdminMutations";
import displayNotification from "../reservation/admin/notification/notification";
import { Tab1 } from "./overview/tabs/tab1";
import { Tab2 } from "./overview/tabs/tab2";
import { Tab3 } from "./overview/tabs/tab3";

const { TabPane } = Tabs;

const OwnerEditPage = () => {
  const { t } = useTranslation("common");
  const { ownerId } = useParams();
  const { path } = useRouteMatch();
  const history = useHistory();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const zoznamMajitelov = useSelector(
    (state) => state?.carOwners?.allCarOwners
  );
  const [loadData, setLoadData] = useState(false);

  const basePath = path.replace(/\/:ownerId(\/edit)?$/, "") || "/admin/car-owners";

  const { owner, loading: ownerLoading, error: ownerError } = useCarOwnerById(
    ownerId,
    loadData
  );
  const { cars } = useOwnerCars(ownerId, loadData);
  const { products } = useOwnerProducts(ownerId, loadData);

  const [triggerUpdateCompany] = useMutation(UPDATE_CAR_OWNER, {
    variables: { id: ownerId, tokenMutation: process.env.REACT_APP_TOKEN_MUTATION },
  });

  useEffect(() => {
    setLoadData(true);
  }, [ownerId]);

  useEffect(() => {
    if (cars.length > 0) {
      dispatch(setAllCars(cars));
      dispatch(setTableTab2Filer(cars));
    }
  }, [cars, dispatch]);

  useEffect(() => {
    if (products.length > 0) {
      dispatch(setCarOwnerAllProducts(products));
      dispatch(setTableFilterCarOwnerProducts(products));
    }
  }, [products, dispatch]);

  useEffect(() => {
    if (owner) {
      form.setFieldsValue({
        company: owner.company,
        firstname: owner.firstname,
        lastname: owner.lastname,
        street: owner.street,
        city: owner.city,
        zip: owner.zip,
        phone: owner.phone,
        email: owner.email,
        ic: owner.ic,
        dic: owner.dic,
        ic_dph: owner.ic_dph,
        reservation_emails: owner.reservation_emails,
        invoice_emails: owner.invoice_emails,
        invoice_maturity: owner.invoice_maturity,
        bonus: owner.bonus,
        // PICK UP: klient ma sluzbu dohodnutu
        pickup: owner.pickup === true,
      });
    }
  }, [owner, form]);

  // Prazdne policko nesmie skoncit ako nula. Number("") je 0, cize by sa
  // splatnost ticho prepisala na nulu dni. Ked hodnota nie je cislo,
  // nepošle sa vobec a stlpec v databaze zostane, ako bol.
  const cislo = (x) => {
    if (x === null || x === undefined || String(x).trim() === "") return undefined;
    const n = Number(x);
    return Number.isFinite(n) ? n : undefined;
  };

  const handleEditOk = (values) => {
    const variables = {
      tokenMutation: process.env.REACT_APP_TOKEN_MUTATION,
      id: ownerId,
      firstname: values.firstname,
      lastname: values.lastname,
      company: values.company,
      zip: values.zip,
      street: values.street,
      city: values.city,
      phone: values.phone,
      email: values.email,
      ic: values.ic,
      dic: values.dic,
      ic_dph: values.ic_dph,
      reservation_emails: values.reservation_emails,
      invoice_emails: values.invoice_emails,
      invoice_maturity: cislo(values.invoice_maturity),
      bonus: cislo(values.bonus),
      pickup: values.pickup === true,
    };

    const message = t("backend.notifications.create");

    triggerUpdateCompany({ variables })
      .then(({ data }) => {
        // Backend vracia ZOZNAM, nie jeden objekt.
        const vysledok = data?.updateCarOwner;
        const ulozeny = Array.isArray(vysledok) ? vysledok[0] : vysledok;

        if (!ulozeny?.id) {
          displayNotification(
            "error",
            message,
            t("backend.notifications.error.recordUpdate")
          );
          return;
        }

        // Zoznam v store drzi pole. Keby sa don vlozil jeden objekt,
        // rozbil by sa, preto sa prepisuje iba ten jeden riadok.
        if (Array.isArray(zoznamMajitelov)) {
          dispatch(
            setAllCarOwners(
              zoznamMajitelov.map((m) =>
                String(m?.id) === String(ownerId) ? { ...m, ...ulozeny } : m
              )
            )
          );
        }

        displayNotification(
          "success",
          message,
          t("backend.notifications.success.recordUpdate")
        );
        history.push(`/admin/car-owners/${ownerId}`);
      })
      // Bez tohto zlyhane ulozenie nic nepovie: tlacidlo len nic neurobi
      // a cloveku to vyzera, ze sa zmena neulozila bez dovodu.
      .catch((chyba) => {
        displayNotification(
          "error",
          message,
          chyba?.message || t("backend.notifications.error.recordUpdate")
        );
      });
  };

  if (ownerLoading && !owner) {
    return (
      <div className="spin">
        <Spin />
      </div>
    );
  }

  if (ownerError && !owner) {
    return (
      <Main>
        <PageHeader ghost title={t("carOwners.list")} />
        <p>{ownerError.message || t("backend.notifications.error.load")}</p>
        <Button type="primary" onClick={() => history.push("/admin/car-owners")}>
          {t("common.back")}
        </Button>
      </Main>
    );
  }

  if (!owner) {
    return null;
  }

  const ownerName = owner.company || owner.firstname || owner.lastname || ownerId;
  const listPath = "/admin/car-owners";
  const breadcrumbRoutes = [
    { path: "/admin", breadcrumbName: t("common.home") },
    { path: listPath, breadcrumbName: t("backend.navigation.carOwners") },
    { path: "", breadcrumbName: ownerName },
  ];

  const breadcrumbItemRender = (route, params, routes) => {
    const isLast = routes.indexOf(route) === routes.length - 1;
    if (isLast) {
      return <span>{route.breadcrumbName}</span>;
    }
    return <Link to={route.path}>{route.breadcrumbName}</Link>;
  };

  const graphqlVariables = {
    tokenGet: process.env.REACT_APP_TOKEN_GET,
    tokenMutation: process.env.REACT_APP_TOKEN_MUTATION,
  };

  return (
    <>
      <PageHeader
        ghost
        title={owner.company + " - " + t("common.edit")}
        routes={breadcrumbRoutes}
        breadcrumbItemRender={breadcrumbItemRender}
        onBack={() => history.push(`/admin/car-owners/${ownerId}`)}
      />
      <Main>
        <Tabs defaultActiveKey="tab1" destroyInactiveTabPane>
          <TabPane tab={t("carOwners.modal.tabs.tab1.title")} key="tab1">
            <Tab1
              t={t}
              data={owner}
              formReadonly={false}
              form={form}
              handleEditOk={handleEditOk}
            />
          </TabPane>
          <TabPane
            tab={t("carOwners.modal.tabs.tab2.title") + " ( " + cars.length + " )"}
            key="tab2"
          >
            <Tab2 t={t} selectedItem={owner} carsData={cars} />
          </TabPane>
          <TabPane tab={t("carOwners.modal.tabs.tab3.title")} key="tab3">
            <Tab3
              t={t}
              selectedItem={owner}
              carsData={cars}
              products={products}
              graphqlVariables={graphqlVariables}
            />
          </TabPane>
        </Tabs>
      </Main>
    </>
  );
};

export default OwnerEditPage;
